<template>
  <div class="capture-review-workflow">
    <!-- Workflow Progress -->
    <div class="workflow-progress">
      <div
        :class="[
          'progress-step',
          {
            active: currentStep === 'review',
            completed: completedSteps.includes('review'),
          },
        ]"
        @click="currentStep = 'review'"
      >
        <div class="step-number">1</div>
        <div class="step-label">Review</div>
      </div>

      <div
        class="progress-connector"
        :class="{ active: completedSteps.includes('review') }"
      ></div>

      <div
        :class="[
          'progress-step',
          {
            active: currentStep === 'approve',
            completed: completedSteps.includes('approve'),
          },
        ]"
        @click="
          currentStep = completedSteps.includes('review')
            ? 'approve'
            : currentStep
        "
      >
        <div class="step-number">2</div>
        <div class="step-label">Approve</div>
      </div>

      <div
        class="progress-connector"
        :class="{ active: completedSteps.includes('approve') }"
      ></div>

      <div
        :class="[
          'progress-step',
          {
            active: currentStep === 'merge',
            completed: completedSteps.includes('merge'),
          },
        ]"
        @click="
          currentStep = completedSteps.includes('approve')
            ? 'merge'
            : currentStep
        "
      >
        <div class="step-number">3</div>
        <div class="step-label">Submit PR</div>
      </div>

      <div
        class="progress-connector"
        :class="{ active: completedSteps.includes('merge') }"
      ></div>

      <div
        :class="[
          'progress-step',
          {
            active: currentStep === 'success',
            completed: completedSteps.includes('success'),
          },
        ]"
      >
        <div class="step-number">4</div>
        <div class="step-label">Complete</div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-panel">
      <div class="spinner"></div>
      <p>{{ loadingMessage }}</p>
    </div>

    <!-- Step 1: Review -->
    <div v-else-if="currentStep === 'review'" class="review-step">
      <!-- Re-comparison Button -->
      <div class="recompare-section">
        <button class="btn-recompare" @click="showExclusionDialog = true">
          <span class="icon">🔄</span>
          Update Exclusions & Re-analyze
        </button>
      </div>

      <!-- Dual Comparison -->
      <DualComparison
        v-if="comparisonData"
        :drift-data="comparisonData.drift"
        :compliance-data="comparisonData.compliance"
        :client-code="clientCode"
        :branch-name="branchName"
        @view-details="openPolicyDetails"
        @mark-reviewed="markPolicyReviewed"
      />

      <!-- Review Progress -->
      <div class="review-progress-panel">
        <h3>📌 Review Progress</h3>
        <div class="progress-bar-container">
          <div
            class="progress-bar"
            :style="{ width: reviewProgressPercent + '%' }"
          ></div>
        </div>
        <p class="progress-text">
          {{ reviewedCount }} of {{ totalReviewableCount }} policies reviewed
          ({{ reviewProgressPercent }}%)
        </p>

        <div class="review-actions">
          <button
            class="btn-secondary"
            @click="markAllReviewed"
            :disabled="reviewedCount === totalReviewableCount"
          >
            Mark All as Reviewed
          </button>

          <button
            class="btn-primary"
            @click="proceedToApproval"
            :disabled="reviewedCount !== totalReviewableCount"
          >
            Continue to Approval →
          </button>
        </div>
      </div>
    </div>

    <!-- Step 2: Approve -->
    <div v-else-if="currentStep === 'approve'" class="approve-step">
      <ApprovalDialog
        :branch-name="branchName"
        :client-code="clientCode"
        :comparison-summary="comparisonSummary"
        @approve="handleApproval"
        @back="currentStep = 'review'"
      />
    </div>

    <!-- Step 3: Merge (Create PR) -->
    <div v-else-if="currentStep === 'merge'" class="merge-step">
      <MergeConfirmation
        :branch-name="branchName"
        :client-code="clientCode"
        :approval-data="approvalData"
        :comparison-summary="comparisonSummary"
        @merge="handleMerge"
        @back="currentStep = 'approve'"
      />
    </div>

    <!-- Step 4: Success -->
    <div v-else-if="currentStep === 'success'" class="success-step">
      <div class="success-panel">
        <div class="success-icon">✅</div>
        <h2>Pull Request Created!</h2>
        <p class="success-message">
          A pull request has been created for
          <strong>{{ clientCode }}</strong> configuration capture.
        </p>

        <div class="merge-details">
          <div class="detail-row">
            <span class="label">Pull Request:</span>
            <span class="value">
              <a :href="mergeResult?.prUrl" target="_blank" class="pr-link">
                #{{ mergeResult?.pullRequestId }}
              </a>
            </span>
          </div>
          <div class="detail-row">
            <span class="label">Status:</span>
            <span class="status-badge pending">
              ⏳ Awaiting Azure DevOps Approval
            </span>
          </div>
          <div class="detail-row">
            <span class="label">Branch:</span>
            <span class="value">{{ branchName }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Created By:</span>
            <span class="value">{{ mergeResult?.createdBy }}</span>
          </div>
        </div>

        <div class="action-buttons">
          <a :href="mergeResult?.prUrl" target="_blank" class="btn-primary">
            View Pull Request in Azure DevOps →
          </a>
        </div>

        <div class="info-box">
          <div class="info-icon">ℹ️</div>
          <div class="info-content">
            <strong>Next Steps:</strong>
            <ul>
              <li>The PR must be approved in Azure DevOps</li>
              <li>Once approved, it will be merged to main</li>
              <li>
                The capture branch will remain visible until merge completes
              </li>
              <li>You'll receive a notification when the merge is complete</li>
            </ul>
          </div>
        </div>

        <p class="redirect-message">
          Returning to capture selector in {{ countdown }} seconds...
        </p>
      </div>
    </div>

    <!-- Policy Detail Modal -->
    <PolicyDetailModal
      v-if="showPolicyModal"
      :policy-name="selectedPolicy.name"
      :drift-details="selectedPolicy.driftDetails"
      :compliance-details="selectedPolicy.complianceDetails"
      :client-code="clientCode"
      :branch-name="branchName"
      @close="showPolicyModal = false"
      @accept-deviation="handleAcceptDeviation"
    />

    <!-- Exclusion Dialog -->
    <div
      v-if="showExclusionDialog"
      class="modal-overlay"
      @click.self="showExclusionDialog = false"
    >
      <div class="exclusion-dialog">
        <div class="dialog-header">
          <h3>Update Excluded Properties</h3>
          <button class="close-btn" @click="showExclusionDialog = false">
            ×
          </button>
        </div>

        <div class="dialog-body">
          <p class="dialog-description">
            Enter property names to exclude from the comparison (one per line).
            These properties will be ignored when checking for deviations.
          </p>

          <div class="tabs">
            <button
              :class="['tab', { active: exclusionTab === 'properties' }]"
              @click="exclusionTab = 'properties'"
            >
              Properties
            </button>
            <button
              :class="['tab', { active: exclusionTab === 'paths' }]"
              @click="exclusionTab = 'paths'"
            >
              Paths
            </button>
          </div>

          <textarea
            v-if="exclusionTab === 'properties'"
            v-model="exclusionPropertiesText"
            placeholder="Example:&#10;createdDateTime&#10;lastModifiedDateTime&#10;id&#10;@odata.type"
            rows="15"
            class="exclusion-textarea"
          ></textarea>

          <textarea
            v-if="exclusionTab === 'paths'"
            v-model="exclusionPathsText"
            placeholder="Example:&#10;/M365Config/Resources/Intune/MobileApps&#10;/M365Config/Resources/AzureAD/Groups"
            rows="15"
            class="exclusion-textarea"
          ></textarea>

          <div class="quick-actions">
            <button class="btn-link" @click="loadDefaultExclusions">
              Load Default Exclusions
            </button>
            <button class="btn-link" @click="clearExclusions">Clear All</button>
          </div>
        </div>

        <div class="dialog-footer">
          <button class="btn-secondary" @click="showExclusionDialog = false">
            Cancel
          </button>
          <button class="btn-primary" @click="applyExclusions">
            Apply & Re-analyze
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DualComparison from "@/views/components/capture/DualComparison.vue";
import ApprovalDialog from "@/views/components/capture/ApprovalDialog.vue";
import MergeConfirmation from "@/views/components/capture/MergeConfirmation.vue";
import PolicyDetailModal from "@/views/components/capture/PolicyDetailModal.vue";
import {
  detectClientDrift,
  compareBaselineVsClientOptimized,
  mergeCaptureToMain,
  fetchFileContent,
  addAcceptedDeviations,
  loadAcceptedDeviationsConfig,
  applyAcceptedDeviationsConfig,
} from "@/azure-devops-api.js";
import {
  DEFAULT_EXCLUDED_PROPERTIES,
  mergeExcludedProperties,
} from "@/utils/excluded-properties.js";
import { getAccessToken } from "@/services/auth-utils.js";

export default {
  name: "CaptureReviewWorkflow",

  components: {
    DualComparison,
    ApprovalDialog,
    MergeConfirmation,
    PolicyDetailModal,
  },

  props: {
    clientCode: {
      type: String,
      required: true,
    },
    branchName: {
      type: String,
      required: true,
    },
    repoId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      // Workflow state
      currentStep: "review",
      completedSteps: [],

      // Comparison data
      comparisonData: null,
      comparisonSummary: null,

      // Review tracking
      reviewedPolicies: new Set(),
      totalReviewableCount: 0,

      // Approval data
      approvalData: null,

      // Merge result
      mergeResult: null,

      // Countdown timer
      countdown: 5,
      countdownInterval: null,

      // UI state
      loading: false,
      loadingMessage: "Loading comparison data...",

      // Modal state
      showPolicyModal: false,
      selectedPolicy: null,

      // Exclusion dialog state
      showExclusionDialog: false,
      exclusionTab: "properties",
      exclusionPropertiesText: "",
      exclusionPathsText: "",

      // Exclusions (paths only - properties come from centralized constant)
      excludedPaths: [],
    };
  },

  computed: {
    reviewedCount() {
      return this.reviewedPolicies.size;
    },

    reviewProgressPercent() {
      if (this.totalReviewableCount === 0) return 0;
      return Math.round((this.reviewedCount / this.totalReviewableCount) * 100);
    },
  },

  async mounted() {
    await this.loadComparisonData();
  },

  beforeUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  },

  watch: {
    branchName: {
      handler() {
        this.resetWorkflow();
        this.loadComparisonData();
      },
    },

    currentStep(newStep) {
      if (newStep === "success") {
        this.startCountdown();
      }
    },
  },

  methods: {
    async loadComparisonData() {
      this.loading = true;
      this.loadingMessage = "Loading comparison data...";

      try {
        const accessToken = await this.getAccessToken();

        console.log("=== LOADING COMPARISON DATA ===");
        console.log(
          "Excluded Properties:",
          DEFAULT_EXCLUDED_PROPERTIES.length,
          "properties"
        );
        console.log("Excluded Paths:", this.excludedPaths);

        let [driftAnalysis, complianceData] = await Promise.all([
          detectClientDrift(
            accessToken,
            this.repoId,
            this.clientCode,
            this.branchName
          ).catch(() => ({
            changes: [],
            summary: { added: 0, modified: 0, deleted: 0 },
          })),
          compareBaselineVsClientOptimized(
            accessToken,
            this.repoId,
            this.clientCode,
            this.branchName,
            DEFAULT_EXCLUDED_PROPERTIES,
            this.excludedPaths
          ),
        ]);

        // 🆕 Load and apply accepted deviations configuration
        console.log("📂 Loading accepted deviations configuration...");

        try {
          const acceptedConfig = await loadAcceptedDeviationsConfig(
            accessToken,
            this.repoId,
            this.clientCode
          );

          if (acceptedConfig?.acceptedDeviations?.length > 0) {
            console.log(
              `🎯 Applying ${acceptedConfig.acceptedDeviations.length} accepted deviations`
            );

            // Apply the accepted deviations config to filter them out
            complianceData = applyAcceptedDeviationsConfig(
              complianceData,
              acceptedConfig
            );

            console.log(
              `✅ Applied accepted deviations - ${
                complianceData.acceptedDeviationsCount || 0
              } filtered out`
            );
          } else {
            console.log("ℹ️ No accepted deviations to apply");
          }
        } catch (error) {
          console.error("⚠️ Error loading accepted deviations config:", error);
          // Continue without accepted deviations if it fails
        }

        console.log("=== COMPARISON RESULTS ===");
        console.log("Drift changes count:", driftAnalysis.changes?.length || 0);
        console.log(
          "Compliance deviations count:",
          complianceData.deviations?.length || 0
        );

        // ENHANCED: For edited files in drift, fetch property-level changes
        const modifiedChanges = driftAnalysis.changes.filter(
          (change) => change.changeType === "edit"
        );

        if (modifiedChanges.length > 0) {
          console.log(
            `Analyzing ${modifiedChanges.length} modified files for property changes...`
          );

          const updatedChanges = await Promise.all(
            modifiedChanges.map(async (change) => {
              try {
                const propertyChanges = await this.compareFileVersions(
                  accessToken,
                  change.item.path,
                  this.branchName,
                  "main"
                );

                return {
                  ...change,
                  propertyChanges: propertyChanges,
                  propertyChangeCount: propertyChanges.length,
                };
              } catch (error) {
                console.error(`Error comparing ${change.item.path}:`, error);
                return {
                  ...change,
                  propertyChanges: [],
                  propertyChangeCount: 0,
                };
              }
            })
          );

          driftAnalysis.changes = driftAnalysis.changes.map((change) => {
            if (change.changeType === "edit") {
              const updated = updatedChanges.find(
                (u) => u.item.path === change.item.path
              );
              return updated || change;
            }
            return change;
          });
        }

        this.comparisonData = {
          drift: driftAnalysis,
          compliance: complianceData,
        };

        this.comparisonSummary = {
          drift: driftAnalysis.summary || { added: 0, modified: 0, deleted: 0 },
          compliance: {
            totalPolicies: complianceData.totalCount || 0,
            compliantPolicies: complianceData.compliantCount || 0,
            deviations: complianceData.deviationCount || 0,
            critical: complianceData.criticalCount || 0,
            high: complianceData.highCount || 0,
          },
        };

        // Calculate UNIQUE reviewable items (deduplicate paths)
        const uniquePaths = new Set();

        (driftAnalysis.changes || []).forEach((change) => {
          if (change.item?.path) {
            uniquePaths.add(change.item.path);
          }
        });

        (complianceData.deviations || []).forEach((deviation) => {
          if (deviation.path) {
            uniquePaths.add(deviation.path);
          }
        });

        this.totalReviewableCount = uniquePaths.size;

        console.log("=== REVIEWABLE COUNT ===");
        console.log("Total reviewable count:", this.totalReviewableCount);
        console.log("  Drift changes:", driftAnalysis.changes?.length || 0);
        console.log(
          "  Compliance deviations:",
          complianceData.deviations?.length || 0
        );
        console.log("  Unique files:", uniquePaths.size);
      } catch (error) {
        console.error("Error loading comparison data:", error);
        alert("Failed to load comparison data. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    async compareFileVersions(
      accessToken,
      filePath,
      captureBranch,
      mainBranch
    ) {
      try {
        const [captureFile, mainFile] = await Promise.all([
          fetchFileContent(accessToken, this.repoId, filePath, captureBranch),
          fetchFileContent(accessToken, this.repoId, filePath, mainBranch),
        ]);

        const captureContent = JSON.parse(captureFile);
        const mainContent = JSON.parse(mainFile);

        return this.findJsonDifferences(mainContent, captureContent);
      } catch (error) {
        console.error("Error comparing file versions:", error);
        return [];
      }
    },

    findJsonDifferences(obj1, obj2, path = "") {
      const differences = [];

      const allKeys = new Set([
        ...Object.keys(obj1 || {}),
        ...Object.keys(obj2 || {}),
      ]);

      allKeys.forEach((key) => {
        const currentPath = path ? `${path}.${key}` : key;
        const val1 = obj1?.[key];
        const val2 = obj2?.[key];

        if (JSON.stringify(val1) === JSON.stringify(val2)) {
          return;
        }

        if (
          typeof val1 === "object" &&
          val1 !== null &&
          typeof val2 === "object" &&
          val2 !== null &&
          !Array.isArray(val1) &&
          !Array.isArray(val2)
        ) {
          differences.push(
            ...this.findJsonDifferences(val1, val2, currentPath)
          );
        } else {
          differences.push({
            property: currentPath,
            oldValue: val1,
            newValue: val2,
            changeType:
              val1 === undefined
                ? "added"
                : val2 === undefined
                ? "removed"
                : "modified",
          });
        }
      });

      return differences;
    },

    async recompareWithExclusions(
      customExcludedProperties,
      customExcludedPaths
    ) {
      this.loading = true;
      this.loadingMessage = "Re-analyzing with updated exclusions...";

      try {
        const accessToken = await this.getAccessToken();

        // ✅ Merge custom properties with defaults
        const mergedProperties = mergeExcludedProperties(
          customExcludedProperties
        );

        console.log("=== RE-COMPARING ===");
        console.log("Default properties:", DEFAULT_EXCLUDED_PROPERTIES.length);
        console.log("Custom properties:", customExcludedProperties.length);
        console.log("Merged properties:", mergedProperties.length);

        let complianceData = await compareBaselineVsClientOptimized(
          accessToken,
          this.repoId,
          this.clientCode,
          this.branchName,
          mergedProperties,
          customExcludedPaths
        );

        // 🆕 Also apply accepted deviations when re-comparing
        console.log("📂 Re-applying accepted deviations...");

        try {
          const acceptedConfig = await loadAcceptedDeviationsConfig(
            accessToken,
            this.repoId,
            this.clientCode
          );

          if (acceptedConfig?.acceptedDeviations?.length > 0) {
            console.log(
              `🎯 Re-applying ${acceptedConfig.acceptedDeviations.length} accepted deviations`
            );

            complianceData = applyAcceptedDeviationsConfig(
              complianceData,
              acceptedConfig
            );

            console.log(
              `✅ Re-applied accepted deviations - ${
                complianceData.acceptedDeviationsCount || 0
              } filtered out`
            );
          } else {
            console.log("ℹ️ No accepted deviations to re-apply");
          }
        } catch (error) {
          console.error("⚠️ Error re-applying accepted deviations:", error);
          // Continue without accepted deviations if it fails
        }

        this.comparisonData.compliance = complianceData;

        this.comparisonSummary.compliance = {
          totalPolicies: complianceData.totalCount || 0,
          compliantPolicies: complianceData.compliantCount || 0,
          deviations: complianceData.deviationCount || 0,
          critical: complianceData.criticalCount || 0,
          high: complianceData.highCount || 0,
        };

        // Calculate UNIQUE reviewable items
        const uniquePaths = new Set();

        (this.comparisonData.drift.changes || []).forEach((change) => {
          if (change.item?.path) {
            uniquePaths.add(change.item.path);
          }
        });

        (complianceData.deviations || []).forEach((deviation) => {
          if (deviation.path) {
            uniquePaths.add(deviation.path);
          }
        });

        this.totalReviewableCount = uniquePaths.size;

        // Reset review tracking
        this.reviewedPolicies = new Set();
      } catch (error) {
        console.error("Error recomparing:", error);
        alert("Failed to re-analyze. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    applyExclusions() {
      const customProperties = this.exclusionPropertiesText
        .split("\n")
        .map((p) => p.trim())
        .filter((p) => p);

      const customPaths = this.exclusionPathsText
        .split("\n")
        .map((p) => p.trim())
        .filter((p) => p);

      // ✅ Pass custom properties to be merged with defaults
      this.recompareWithExclusions(customProperties, customPaths);
      this.showExclusionDialog = false;
    },

    loadDefaultExclusions() {
      if (this.exclusionTab === "properties") {
        // ✅ Using imported constant
        this.exclusionPropertiesText = DEFAULT_EXCLUDED_PROPERTIES.join("\n");
      }
    },

    clearExclusions() {
      if (this.exclusionTab === "properties") {
        this.exclusionPropertiesText = "";
      } else {
        this.exclusionPathsText = "";
      }
    },

    openPolicyDetails(policyData) {
      if (policyData.type === "drift") {
        const change = policyData.item;

        this.selectedPolicy = {
          name: this.getFileName(change.item.path),
          path: change.item.path,
          category: this.getCategoryFromPath(change.item.path),
          severity: "info",
          driftDetails: {
            changeType: change.changeType,
            path: change.item.path,
            objectId: change.item.objectId,
            propertyChanges: change.propertyChanges || [],
            propertyChangeCount: change.propertyChangeCount || 0,
          },
          complianceDetails: null,
        };
      } else {
        const fullDeviationData =
          this.comparisonData.compliance.deviations?.find(
            (d) => d.path === policyData.path
          );

        this.selectedPolicy = {
          name: policyData.policyName || policyData.name,
          path: policyData.path,
          category: policyData.category,
          severity: policyData.severity || policyData.highestSeverity || 'low',
          driftDetails: null,
          complianceDetails: fullDeviationData,
        };
      }

      this.showPolicyModal = true;
    },

    getFileName(path) {
      return path.split("/").pop().replace(".json", "");
    },

    getCategoryFromPath(path) {
      const match = path.match(/\/M365Config\/Resources\/([^/]+)/i);
      return match ? match[1] : "Other";
    },

    markPolicyReviewed(policyIdentifier) {
      this.reviewedPolicies.add(policyIdentifier);
      this.$forceUpdate();
    },

    markAllReviewed() {
      console.log("=== MARK ALL AS REVIEWED ===");

      const driftPaths = [];
      const compliancePaths = [];

      if (this.comparisonData.drift.changes) {
        this.comparisonData.drift.changes.forEach((change) => {
          if (change.item?.path) {
            this.reviewedPolicies.add(change.item.path);
            driftPaths.push(change.item.path);
          }
        });
      }

      if (this.comparisonData.compliance.deviations) {
        this.comparisonData.compliance.deviations.forEach((deviation) => {
          if (deviation.path) {
            this.reviewedPolicies.add(deviation.path);
            compliancePaths.push(deviation.path);
          }
        });
      }

      // Find duplicates
      const driftSet = new Set(driftPaths);
      const duplicates = compliancePaths.filter((path) => driftSet.has(path));

      if (duplicates.length > 0) {
        console.log("🔴 DUPLICATE PATH FOUND (in both drift and compliance):");
        duplicates.forEach((path) => console.log("  →", path));
      }

      console.log("=== FINAL COUNTS ===");
      console.log("Total items in Set:", this.reviewedPolicies.size);
      console.log("Expected total:", this.totalReviewableCount);

      this.$forceUpdate();
    },

    proceedToApproval() {
      if (this.reviewedCount !== this.totalReviewableCount) {
        alert("Please review all policies before proceeding to approval.");
        return;
      }

      this.completedSteps.push("review");
      this.currentStep = "approve";
    },

    handleApproval(approvalData) {
      this.approvalData = approvalData;
      this.completedSteps.push("approve");
      this.currentStep = "merge";
    },

    async handleMerge() {
      this.loading = true;
      this.loadingMessage = "Creating pull request...";

      try {
        const accessToken = await this.getAccessToken();

        const result = await mergeCaptureToMain(
          accessToken,
          this.repoId,
          this.branchName,
          this.clientCode,
          this.approvalData
        );

        this.completedSteps.push("merge");
        this.completedSteps.push("success");
        this.mergeResult = result;
        this.currentStep = "success";
      } catch (error) {
        console.error("Error creating pull request:", error);
        alert(
          `Failed to create pull request: ${error.message || "Unknown error"}`
        );
      } finally {
        this.loading = false;
      }
    },

    startCountdown() {
      this.countdown = 5;
      this.countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownInterval);
          this.$emit("merge-complete", this.mergeResult);
        }
      }, 1000);
    },

    async handleAcceptDeviation(deviationData) {
      console.log("🟢 ========== PARENT: Event Received ==========");
      console.log("🟢 Received data:", deviationData);
      console.log("🟢 Path:", deviationData?.path);
      console.log("🟢 Policy name:", deviationData?.policyName);
      console.log(
        "🟢 Selected deviations count:",
        deviationData?.selectedDeviations?.length
      );

      // Close modal immediately
      this.showPolicyModal = false;

      try {
        this.loading = true;
        this.loadingMessage = "Saving accepted deviations...";

        const accessToken = await this.getAccessToken();
        console.log("🟢 Access token obtained:", accessToken ? "YES" : "NO");

        await addAcceptedDeviations(
          accessToken,
          this.repoId,
          this.clientCode,
          deviationData
        );

        console.log("🟢 ✅ Deviations accepted and saved");

        alert(
          `Successfully accepted ${deviationData.selectedDeviations.length} deviation(s) for ${deviationData.policyName}`
        );

        await this.loadComparisonData();

        console.log("🟢 ========== PARENT: Complete ==========");
      } catch (error) {
        console.error("🟢 ❌ Error accepting deviations:", error);
        console.error("🟢 Error stack:", error.stack);
        alert("Failed to save accepted deviations. Please try again.");
      } finally {
        this.loading = false;
        this.loadingMessage = "";
      }
    },

    resetWorkflow() {
      this.currentStep = "review";
      this.completedSteps = [];
      this.reviewedPolicies = new Set();
      this.approvalData = null;
      this.mergeResult = null;
      this.comparisonData = null;
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
    },

    getAccessToken,
  },
};
</script>

<style scoped>
.capture-review-workflow {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.workflow-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  opacity: 0.5;
  transition: all 0.3s;
}

.progress-step.active,
.progress-step.completed {
  opacity: 1;
}

.progress-step:not(.active):not(.completed) {
  cursor: not-allowed;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e0e0e0;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s;
}

.progress-step.active .step-number {
  background: #007bff;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.progress-step.completed .step-number {
  background: #28a745;
  color: white;
}

.progress-step.completed .step-number::before {
  content: "✓";
}

.step-label {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
}

.progress-connector {
  width: 80px;
  height: 2px;
  background: #e0e0e0;
  margin: 0 16px;
  transition: all 0.3s;
}

.progress-connector.active {
  background: #28a745;
}

.loading-panel {
  padding: 80px 20px;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.review-step {
  padding: 0;
}

.approve-step,
.merge-step {
  padding: 24px;
}

.success-step {
  padding: 60px 24px;
  text-align: center;
  min-height: 500px;
}

.success-panel {
  max-width: 600px;
  margin: 0 auto;
}

.success-icon {
  font-size: 72px;
  margin-bottom: 24px;
  animation: successPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes successPop {
  0% {
    transform: scale(0) rotate(-180deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(10deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.success-panel h2 {
  margin: 0 0 12px 0;
  font-size: 28px;
  color: #28a745;
  font-weight: 700;
}

.success-message {
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
  line-height: 1.5;
}

.merge-details {
  background: #f8f9fa;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 24px;
  text-align: left;
  border: 1px solid #e0e0e0;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .label {
  font-weight: 600;
  color: #666;
  font-size: 14px;
}

.detail-row .value {
  color: #1a1a1a;
  font-size: 14px;
}

.pr-link {
  color: #007bff;
  text-decoration: none;
  font-weight: 600;
}

.pr-link:hover {
  text-decoration: underline;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  display: inline-block;
}

.status-badge.pending {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.action-buttons {
  margin: 32px 0;
}

.info-box {
  background: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  padding: 20px;
  margin: 24px 0;
  text-align: left;
  display: flex;
  gap: 16px;
}

.info-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.info-content strong {
  display: block;
  margin-bottom: 12px;
  color: #004085;
  font-size: 15px;
}

.info-content ul {
  margin: 0;
  padding-left: 20px;
  color: #004085;
}

.info-content li {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
}

.redirect-message {
  font-size: 14px;
  color: #999;
  font-style: italic;
  margin-top: 24px;
}

.review-progress-panel {
  background: #f8f9fa;
  padding: 24px;
  border-radius: 8px;
  margin: 24px;
}

.review-progress-panel h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #0056b3);
  transition: width 0.3s ease;
}

.progress-text {
  margin: 0 0 20px 0;
  font-size: 14px;
  color: #666;
}

.review-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-block;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn-secondary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.recompare-section {
  padding: 16px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.btn-recompare {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 2px solid #007bff;
  border-radius: 4px;
  color: #007bff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-recompare:hover {
  background: #007bff;
  color: white;
}

.btn-recompare .icon {
  font-size: 16px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.exclusion-dialog {
  background: white;
  border-radius: 8px;
  width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 2px solid #e0e0e0;
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: #666;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #dc3545;
}

.dialog-body {
  padding: 24px;
  overflow: auto;
  flex: 1;
}

.dialog-description {
  margin: 0 0 20px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  border-bottom: 2px solid #e0e0e0;
}

.tab {
  padding: 10px 16px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  transition: all 0.2s;
}

.tab:hover {
  color: #007bff;
}

.tab.active {
  color: #007bff;
  border-bottom-color: #007bff;
}

.exclusion-textarea {
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  font-family: "Courier New", monospace;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.2s;
}

.exclusion-textarea:focus {
  outline: none;
  border-color: #007bff;
}

.quick-actions {
  display: flex;
  gap: 16px;
  margin-top: 12px;
}

.btn-link {
  background: none;
  border: none;
  color: #007bff;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

.btn-link:hover {
  color: #0056b3;
}

.dialog-footer {
  padding: 16px 24px;
  border-top: 2px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #f8f9fa;
}
</style>
